// SmartCaptureModal — Jul 2026. Review card for the Capture Pad's "Smart
// create": the BYOK LLM classified dictated text into calendar items /
// bank questions / notes. NOTHING commits without the user accepting here.
//
// Feb 25, 2026 (Randy pt89c-xxviii) — EDITABLE PERSON VIEW. Items are now
// grouped by person, and each schedulable item can be expanded to (a) set
// ALARMS that ride on that one instance and (b) add a DESCRIPTION. On commit
// the description saves to the matching MASTER chore (family_chores) so it
// becomes the household's standing detail; alarms stay instance-only.
import { useMemo, useState } from "react";
import { X, Check, Sparkle, Spinner, ChatCircleText, BellRinging, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { DOW_MIN } from "@/components/CalendarSection";
import { REMINDER_OPTS } from "@/lib/calendarConstants";

const TYPE_LABEL = {
  event: { label: "EVENT", cls: "bg-[#EAF0EB] text-[#4A5D4E]" },
  task: { label: "TASK", cls: "bg-[#F8F1E2] text-[#8A6A24]" },
  routine: { label: "ROUTINE", cls: "bg-[#E3EFF7] text-[#2C6E92]" },
  ping: { label: "PING", cls: "bg-[#FBEBE4] text-[#C16E5A]" },
  bank_question: { label: "BANK QUESTION", cls: "bg-[#F0F1EE] text-[#1C1B1A]" },
  note: { label: "JUST A NOTE", cls: "bg-[#F0F1EE] text-[#6B6A68]" },
};
const EVERYONE = "__everyone__";
const slug = (s) => (s || "everyone").toString().toLowerCase().replace(/[^a-z0-9]+/g, "-");
const DETAILABLE = new Set(["event", "task", "routine"]);

export default function SmartCaptureModal({ siloId, items: initial, sourceText = "", onAskQA, onSaveAsPing, onClose, onCommitted }) {
  const [items, setItems] = useState(() =>
    (initial || []).map((it, i) => ({ ...it, _idx: i, _include: (it.type || "note") !== "note", _expanded: false })));
  const [busy, setBusy] = useState(false);

  const upd = (idx, patch) => setItems((cur) => cur.map((it) => (it._idx === idx ? { ...it, ...patch } : it)));
  const toggleExpand = (idx) => setItems((cur) => cur.map((it) => (it._idx === idx ? { ...it, _expanded: !it._expanded } : it)));
  const toggleReminder = (idx, v) => setItems((cur) => cur.map((it) => {
    if (it._idx !== idx) return it;
    const has = (it.reminders || []).includes(v);
    return { ...it, reminders: has ? (it.reminders || []).filter((x) => x !== v) : [...(it.reminders || []), v] };
  }));
  const setAllInclude = (v) => setItems((cur) => cur.map((it) => (it.type === "note" ? it : { ...it, _include: v })));

  const includable = items.filter((it) => it._include && it.type !== "note");
  const bulkTotal = items.filter((it) => it.type !== "note").length;

  // Group schedulable items by person (assignee_name); notes go to the end.
  const { groups, notes } = useMemo(() => {
    const map = new Map();
    const noteList = [];
    for (const it of items) {
      if (it.type === "note") { noteList.push(it); continue; }
      const key = it.assignee_name || EVERYONE;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    }
    // Order: named people first (insertion order), then Everyone/Unassigned.
    const ordered = [...map.entries()].sort((a, b) => (a[0] === EVERYONE ? 1 : 0) - (b[0] === EVERYONE ? 1 : 0));
    return { groups: ordered, notes: noteList };
  }, [items]);

  const looksLikeQuestion = /\?|^\s*(what|when|where|who|why|how|should|could|can|would|give me|suggest|find|any\b|is there|do i|am i)/i
    .test((sourceText || "").trim());
  const showQuestionCta = includable.length === 0 && looksLikeQuestion && (onAskQA || onSaveAsPing);

  const commit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/calendar/${siloId}/smart-capture/commit`, {
        items: includable.map(({ _idx, _include, _expanded, ...rest }) => rest),
        tz_offset_minutes: new Date().getTimezoneOffset(),
      });
      const n = (r.data.created || []).length;
      toast.success(r.data.pending
        ? `Sent ${n} item${n === 1 ? "" : "s"} to the calendar owner for approval.`
        : `Created ${n} item${n === 1 ? "" : "s"} — find them in the calendar and bank below.`);
      window.dispatchEvent(new Event("mysilo:calendar-refresh"));
      onCommitted(r.data.created || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't create the items.");
    } finally { setBusy(false); }
  };

  const renderItem = (it) => {
    const meta = TYPE_LABEL[it.type] || TYPE_LABEL.note;
    const isNote = it.type === "note";
    const detailable = DETAILABLE.has(it.type);
    const alarmCount = (it.reminders || []).length;
    return (
      <div key={it._idx} className={`border hairline rounded-md p-3 ${it._include && !isNote ? "bg-white" : "bg-[#FBFAF6] opacity-70"}`} data-testid={`smart-item-${it._idx}`}>
        <div className="flex items-center gap-2 mb-1.5">
          {!isNote && (
            <button onClick={() => upd(it._idx, { _include: !it._include })} data-testid={`smart-item-include-${it._idx}`}
              className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${it._include ? "bg-[#4A5D4E] border-[#4A5D4E] text-white" : "border-[#B5B3AE]"}`}>
              {it._include && <Check size={10} weight="bold" />}
            </button>
          )}
          <span className={`text-[9px] font-semibold tracking-[0.14em] px-1.5 py-0.5 rounded ${meta.cls}`}>{meta.label}</span>
          {(it.date || it.time) && (
            <span className="text-[10px] text-[#6B6A68]">
              {it.date || ""}{it.time ? ` · ${it.time}` : ""}{(it.dow || []).length ? ` · ${(it.dow || []).map((d) => DOW_MIN[d]).join("·")}` : ""}
            </span>
          )}
          {detailable && (
            <button onClick={() => toggleExpand(it._idx)} data-testid={`smart-item-details-${it._idx}`}
              className="ml-auto text-[10px] text-[#4A5D4E] flex items-center gap-1 hover:underline shrink-0">
              {alarmCount > 0 && <span className="flex items-center gap-0.5 text-[#C16E5A]"><BellRinging size={11} weight="fill" />{alarmCount}</span>}
              {(it.description || "").trim() && <span className="w-1.5 h-1.5 rounded-full bg-[#4A5D4E]" title="Has a description" />}
              Details <CaretDown size={10} className={it._expanded ? "rotate-180" : ""} />
            </button>
          )}
        </div>
        {isNote ? (
          <div className="text-xs text-[#6B6A68] leading-relaxed">{it.text}</div>
        ) : (
          <div className="space-y-1.5">
            <input
              value={it.type === "ping" || it.type === "bank_question" ? (it.question || "") : (it.title || "")}
              onChange={(e) => upd(it._idx, it.type === "ping" || it.type === "bank_question" ? { question: e.target.value } : { title: e.target.value })}
              data-testid={`smart-item-text-${it._idx}`}
              className="w-full px-2.5 py-1.5 border hairline rounded text-sm bg-[#FDFCF8] focus:outline-none focus:border-[#4A5D4E]" />
            {it.type !== "bank_question" && (
              <div className="flex gap-1.5">
                {(it.type === "event" || it.type === "task") && (
                  <input type="date" value={it.date || ""} onChange={(e) => upd(it._idx, { date: e.target.value })}
                    className="px-2 py-1 border hairline rounded text-[11px] bg-white" />
                )}
                <input type="time" value={it.time || ""} onChange={(e) => upd(it._idx, { time: e.target.value })}
                  className="px-2 py-1 border hairline rounded text-[11px] bg-white" />
              </div>
            )}
          </div>
        )}
        {detailable && it._expanded && (
          <div className="mt-2.5 pt-2.5 border-t hairline space-y-2.5" data-testid={`smart-item-detail-panel-${it._idx}`}>
            <div>
              <span className="text-[9px] uppercase tracking-[0.16em] text-[#6B6A68] flex items-center gap-1">
                <BellRinging size={11} weight="duotone" className="text-[#C16E5A]" /> Alarms — this occurrence only
              </span>
              <div className="flex flex-wrap gap-1 mt-1">
                {REMINDER_OPTS.map((o) => (
                  <button key={o.v} onClick={() => toggleReminder(it._idx, o.v)}
                    data-testid={`smart-item-alarm-${it._idx}-${o.v}`}
                    className={`text-[10px] px-2 py-1 rounded ${(it.reminders || []).includes(o.v) ? "bg-[#1C1B1A] text-white" : "bg-white border hairline text-[#6B6A68]"}`}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <span className="text-[9px] uppercase tracking-[0.16em] text-[#6B6A68]">
                {it.type === "event" ? "Description — this event" : "Description — saves to the master chore"}
              </span>
              <textarea value={it.description || ""} onChange={(e) => upd(it._idx, { description: e.target.value })} rows={2}
                data-testid={`smart-item-desc-${it._idx}`}
                placeholder={it.type === "event" ? "Notes for this event" : "e.g. Rinse the bin, put liners back on — saved to this chore for every future plan"}
                className="mt-1 w-full px-2.5 py-1.5 border hairline rounded text-xs bg-white focus:outline-none focus:border-[#4A5D4E]" />
              {it.type !== "event" && (
                <p className="text-[9px] text-[#8A6A24] mt-0.5 leading-snug">
                  Alarms stay on this one instance. The description is written back to the chore in your master list, so future plans carry it too.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose} data-testid="smart-capture-modal">
      <div className="bg-[#FDFCF8] rounded-2xl w-full max-w-lg shadow-2xl border hairline max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b hairline flex items-center justify-between sticky top-0 bg-[#FDFCF8] z-10">
          <h2 className="font-serif-display text-lg flex items-center gap-2">
            <Sparkle size={16} weight="duotone" className="text-[#C19A4A]" /> I found {items.length} thing{items.length === 1 ? "" : "s"}
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#F0F1EE]" data-testid="smart-capture-close"><X size={18} /></button>
        </div>
        {bulkTotal > 3 && (
          <div className="px-5 py-2 border-b hairline bg-[#FBFAF6] flex items-center gap-2 sticky top-[52px] z-[9]" data-testid="smart-capture-bulk-chips">
            <span className="text-[10px] uppercase tracking-[0.14em] text-[#6B6A68] font-medium">Bulk</span>
            <button onClick={() => setAllInclude(true)} data-testid="smart-capture-select-all"
              className="text-[11px] px-2.5 py-1 rounded-full border hairline hover:border-[#4A5D4E] hover:text-[#4A5D4E] text-[#1C1B1A] transition">
              Select all
            </button>
            <button onClick={() => setAllInclude(false)} data-testid="smart-capture-deselect-all"
              className="text-[11px] px-2.5 py-1 rounded-full border hairline hover:border-[#C16E5A] hover:text-[#C16E5A] text-[#1C1B1A] transition">
              Deselect all
            </button>
            <span className="flex-1" />
            <span className="text-[11px] text-[#6B6A68]" data-testid="smart-capture-bulk-count">
              {includable.length} of {bulkTotal} selected
            </span>
          </div>
        )}
        <div className="p-4 space-y-4">
          {showQuestionCta && (
            <div className="border border-[#C19A4A] rounded-md p-3 bg-[#FDF9EF]" data-testid="smart-capture-question-cta">
              <div className="text-sm text-[#1C1B1A] font-medium mb-1">That looks like a question, not something to schedule.</div>
              <p className="text-xs text-[#6B6A68] leading-relaxed mb-2.5">
                Smart create only turns your words into calendar items. For an <strong>answer</strong> —
                open slots, ideas, advice — your silo's Q&amp;A knows your live calendar. Or make it a
                recurring <strong>ping</strong> so the answer comes to your phone on a schedule.
              </p>
              <div className="flex flex-wrap gap-2">
                {onAskQA && (
                  <button onClick={() => onAskQA(sourceText)} data-testid="smart-capture-ask-qa"
                    className="text-xs px-3 py-2 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] flex items-center gap-1.5">
                    <ChatCircleText size={13} weight="duotone" /> Ask it in the Q&amp;A now
                  </button>
                )}
                {onSaveAsPing && (
                  <button onClick={() => onSaveAsPing(sourceText)} data-testid="smart-capture-save-ping"
                    className="text-xs px-3 py-2 rounded-md border border-[#C16E5A] text-[#C16E5A] hover:bg-[#FBEBE4] flex items-center gap-1.5">
                    <BellRinging size={13} weight="duotone" /> Save it as a recurring ping
                  </button>
                )}
              </div>
            </div>
          )}
          {items.length === 0 && !showQuestionCta && <div className="text-sm text-[#6B6A68] italic">Nothing schedulable detected — keep it as a note and push it to the silo instead.</div>}

          {groups.map(([person, groupItems]) => (
            <div key={person} data-testid={`smart-capture-group-${slug(person)}`}>
              <div className="flex items-center gap-2 mb-1.5 px-0.5">
                <span className="text-[11px] font-semibold tracking-[0.1em] uppercase text-[#4A5D4E]" data-testid={`smart-capture-group-name-${slug(person)}`}>
                  {person === EVERYONE ? "Everyone / Unassigned" : person}
                </span>
                <span className="h-px flex-1 bg-[#E5E2DA]" />
                <span className="text-[10px] text-[#8A8A88]">{groupItems.length}</span>
              </div>
              <div className="space-y-2.5">{groupItems.map(renderItem)}</div>
            </div>
          ))}

          {notes.length > 0 && (
            <div data-testid="smart-capture-group-notes">
              <div className="flex items-center gap-2 mb-1.5 px-0.5">
                <span className="text-[11px] font-semibold tracking-[0.1em] uppercase text-[#8A8A88]">Notes (not scheduled)</span>
                <span className="h-px flex-1 bg-[#E5E2DA]" />
              </div>
              <div className="space-y-2.5">{notes.map(renderItem)}</div>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t hairline flex items-center gap-2 sticky bottom-0 bg-[#FDFCF8] pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
          <span className="text-[11px] text-[#6B6A68]">{includable.length} selected</span>
          <span className="flex-1" />
          <button onClick={onClose} className="text-xs px-3 py-2 rounded text-[#6B6A68]">Cancel</button>
          <button onClick={commit} disabled={busy || includable.length === 0} data-testid="smart-capture-commit"
            className="text-xs px-4 py-2 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1.5">
            {busy ? <Spinner size={12} className="animate-spin" /> : <Check size={12} weight="bold" />} Create {includable.length} item{includable.length === 1 ? "" : "s"}
          </button>
        </div>
      </div>
    </div>
  );
}
